/* eslint-disable camelcase */
import Categories from 'ai/constants/availabilityPlacements';
import { ENTRYPOINTS } from 'ai/constants/superchat';
import {
    getGENAIAnonymousId, setGENAIAnonymousId, getAssociatedBiId, getGENAISession
} from 'ai/utils/sessionStorage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import { getCachedSrpSearchTerms } from 'ai/utils/srpSearchTermsCache';
import UUIDv4 from 'utils/UUID';
import languageLocaleUtils from 'utils/LanguageLocale';
import PageTemplateType from 'constants/PageTemplateType';
import RCPSCookies from 'utils/RCPSCookies';
import Location from 'utils/Location';
const { getCurrentLanguage } = languageLocaleUtils;

function getSearchKeyword(page) {
    return page?.search?.keyword || new URLSearchParams(window.location.search).get('keyword');
}

function hasCategoryId(obj, availableCategories) {
    let current = obj;

    while (current) {
        if (availableCategories.some(category => category.id === current.categoryId)) {
            return true;
        }

        current = current.parentCategory;
    }

    return false;
}

function getAllAvailableCategories() {
    const CATEGORIES_AVAILABLE = [
        ...Categories.GENAI_CATEGORIES_HAIR,
        ...Categories.GENAI_CATEGORIES_SKINCARE,
        ...Categories.GENAI_CATEGORIES_MAKEUP,
        ...Categories.GENAI_CATEGORIES_BATH_AND_BODY,
        ...Categories.GENAI_CATEGORIES_FRAGRANCE
    ];

    return CATEGORIES_AVAILABLE;
}

function isAIBeautyChatCategoryFragrance(categoryId) {
    if (!categoryId) {
        return false;
    }

    const AVAILABLE_CATEGORIES = Categories.GENAI_CATEGORIES_FRAGRANCE;

    return AVAILABLE_CATEGORIES.some(category => category.id === categoryId);
}

function isAIBeautyChatEnabledPDP(product) {
    if (!product || !product.parentCategory) {
        return false;
    }

    const AVAILABLE_CATEGORIES = getAllAvailableCategories();
    const isGenAIChatPDPEnabled = hasCategoryId(product.parentCategory, AVAILABLE_CATEGORIES);

    return isGenAIChatPDPEnabled;
}

function isSkincareCategory(categoryId) {
    if (!categoryId) {
        return false;
    }

    return Categories.GENAI_CATEGORIES_SKINCARE.some(category => category.id === categoryId);
}

function isAIBeautyChatEnabledPLP(categoryId) {
    if (!categoryId) {
        return false;
    }

    const AVAILABLE_CATEGORIES = getAllAvailableCategories();

    const enablePLP = AVAILABLE_CATEGORIES.some(category => category.id === categoryId);

    if (enablePLP) {
        return true;
    }

    return false;
}

function isAIBeautyChatEnabledSRP(pageType, searchTerm, terms) {
    if (pageType !== 'searchResults') {
        return false;
    }

    if (!searchTerm || searchTerm.trim() === '') {
        return false;
    }

    // GAI-7279: the list of eligible search terms is fetched from the entrypoint prompts
    // service and cached locally for 24h (see srpSearchTermsCache). Callers that need
    // reactivity (the SRP grid selector) pass the terms from Redux so the grid re-evaluates
    // when they arrive; the synchronous cache is used both when no terms are passed and when
    // Redux still holds its empty initial value (so a warm cache renders without waiting for
    // the thunk).
    const hasProvidedTerms = Array.isArray(terms) && terms.length > 0;
    const availableTerms = hasProvidedTerms ? terms : getCachedSrpSearchTerms();

    if (!Array.isArray(availableTerms) || availableTerms.length === 0) {
        return false;
    }

    const trimmedSearchTerm = searchTerm.trim();
    const currentLanguage = getCurrentLanguage();

    if (currentLanguage === 'FR') {
        // Accent-insensitive match for French terms (cached terms are lowercased).
        return availableTerms.some(term => term.localeCompare(trimmedSearchTerm, 'fr', { sensitivity: 'base' }) === 0);
    }

    // EN or other - case-insensitive match (cached terms are already lowercased).
    return availableTerms.some(term => term === trimmedSearchTerm.toLowerCase());
}

function getUUID() {
    const token = Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN);
    let uuid;

    if (token) {
        const tokenPayload = token.split('.')[1];
        const userTokenData = JSON.parse(atob(tokenPayload.replace(/-/g, '+').replace(/_/g, '/')));

        if (userTokenData?.AuthData?.uuid) {
            uuid = JSON.stringify(userTokenData?.AuthData?.uuid).replace(/\"/g, '');
        }
    }

    return uuid;
}

function getEcid() {
    try {
        if (typeof visitor !== 'undefined' && typeof visitor.getMarketingCloudVisitorID === 'function') {
            return visitor.getMarketingCloudVisitorID() || '';
        }
    } catch (e) {
        Sephora.logger.verbose('SuperChat: Failed to retrieve ECID:', e);
    }

    return '';
}

function getAnonymousId(currentBiId = null) {
    const storedAnonymousId = getGENAIAnonymousId();
    const associatedBiId = getAssociatedBiId();

    // Check if user has switched accounts
    if (currentBiId && associatedBiId && currentBiId !== associatedBiId) {
        // User switched accounts - generate new anonymousId
        Sephora.logger.verbose(`SuperChat: User account switched from ${associatedBiId} to ${currentBiId}, generating new anonymousId`);
        const newAnonymousId = UUIDv4();
        setGENAIAnonymousId(newAnonymousId, currentBiId);

        return newAnonymousId;
    }

    // If we have a stored anonymousId and BiId matches (or no BiId check needed), return it
    if (storedAnonymousId) {
        // Update BiId association if user just logged in and we didn't have BiId before
        if (currentBiId && !associatedBiId) {
            setGENAIAnonymousId(storedAnonymousId, currentBiId);
        }

        return storedAnonymousId;
    }

    // No stored anonymousId - try to get UUID from auth token
    const uuid = getUUID();

    if (uuid) {
        setGENAIAnonymousId(uuid, currentBiId);

        return uuid;
    }

    // Generate new UUID as fallback
    const generatedUUID = UUIDv4();
    setGENAIAnonymousId(generatedUUID, currentBiId);

    return generatedUUID;
}

function isAIBeautyGiftEnabled(categoryId) {
    if (!categoryId) {
        return false;
    }

    const AVAILABLE_CATEGORIES = Categories.GENAI_CATEGORIES_GIFT;

    const enablePLP = AVAILABLE_CATEGORIES.some(category => category.id === categoryId);

    return enablePLP;
}

function getContextAwarePlacement(page) {
    const templateType = page?.templateInformation?.template;

    if (templateType === PageTemplateType.Search) {
        return 'SRP';
    } else if (templateType === PageTemplateType.ProductPage) {
        return 'PDP';
    } else if (templateType === PageTemplateType.NthCategory || templateType === PageTemplateType.Category) {
        return 'PLP';
    }

    return null;
}

function getNewEntryPoint(message, page) {
    const contextPlacement = getContextAwarePlacement(page);
    let newEntryPointData;

    switch (contextPlacement) {
        case 'PDP': {
            const product = page?.product;
            const isAIBeautyChatEnabled = isAIBeautyChatEnabledPDP(product, true);

            if (isAIBeautyChatEnabled) {
                newEntryPointData = {
                    entrypoint: ENTRYPOINTS.PDP,
                    products: [
                        {
                            product_id: product?.productId,
                            sku_id: product?.currentSku.skuId
                        }
                    ],
                    selectedPrompt: message
                };
            }

            break;
        }
        case 'PLP': {
            const categoryId = page?.nthCategory?.categoryId;
            const rootCategory = page?.nthCategory?.categories?.at()?.categoryId;
            const products = page?.nthCategory?.products;

            if (
                isAIBeautyChatEnabledPLP(categoryId) ||
                isAIBeautyChatEnabledPLP(rootCategory) ||
                isAIBeautyChatCategoryFragrance(categoryId) ||
                isAIBeautyChatCategoryFragrance(rootCategory)
            ) {
                newEntryPointData = {
                    entrypoint: ENTRYPOINTS.PLP,
                    category_id: categoryId,
                    products: products
                        ?.filter(product => product.currentSku)
                        ?.slice(0, 30)
                        .map(product => ({
                            product_id: product.productId,
                            sku_id: product.currentSku.skuId
                        })),
                    selectedPrompt: message
                };
            }

            break;
        }
        case 'SRP': {
            const searchKeyword = getSearchKeyword(page);
            const products = page?.search?.products;

            if (isAIBeautyChatEnabledSRP('searchResults', searchKeyword)) {
                newEntryPointData = {
                    entrypoint: ENTRYPOINTS.SRP,
                    products: products
                        ?.filter(product => product.currentSku)
                        ?.slice(0, 30)
                        .map(product => ({
                            product_id: product.productId,
                            sku_id: product.currentSku.skuId
                        })),
                    selectedPrompt: message,
                    search_term: searchKeyword
                };
            }

            break;
        }
        default: {
            newEntryPointData = {
                entrypoint: ENTRYPOINTS.DEFAULT,
                selectedPrompt: message
            };

            // In next sprints we will introduce default entryPoint
            break;
        }
    }

    return newEntryPointData;
}

function contextHasChanged(page) {
    const session = getGENAISession();
    const contextPlacement = getContextAwarePlacement(page);

    let hasChanged = true;

    if (session?.entrypoint === ENTRYPOINTS.PLP && contextPlacement === ENTRYPOINTS.PLP && session?.category_id === page?.nthCategory?.categoryId) {
        hasChanged = false;
    } else if (
        session?.entrypoint === ENTRYPOINTS.PDP &&
        contextPlacement === ENTRYPOINTS.PDP &&
        (session?.products?.[0]?.product_id === page?.product?.productId || session?.products?.[0]?.sku_id === page?.product?.currentSku?.skuId)
    ) {
        hasChanged = false;
    } else if (session?.entrypoint === ENTRYPOINTS.SRP && contextPlacement === ENTRYPOINTS.SRP && session?.search_term === getSearchKeyword(page)) {
        hasChanged = false;
    } else if (session?.entrypoint === ENTRYPOINTS.DEFAULT && !contextPlacement) {
        hasChanged = false;
    }

    return hasChanged;
}

function getCategoryLabelKey(page) {
    if (!page) {
        return null;
    }

    const nthCategory = page?.nthCategory;

    if (!nthCategory) {
        return null;
    }

    const categoryId = nthCategory.categoryId;
    const rootCategoryId = nthCategory.categories?.[0]?.categoryId;
    const parentCategory = nthCategory.parentCategory;

    /*
     * Determines the appropriate category label key by checking the current category,
     * root category, and all parent categories in the hierarchy.
     *
     * This is necessary because categories can be nested (e.g., Hair > Hair Dryers > Blow Dryers),
     * and we need to identify the top-level category (e.g., Hair) to show the correct label.
     *
     * Examples:
     * - /shop/hair-products -> checks 'hair-products' category and its parents, finds 'hair' -> returns 'topHairCareQuestions'
     * - /shop/hair-dryers-blow-dryers -> checks 'hair-dryers-blow-dryers', traverses parent chain to 'hair' -> returns 'topHairCareQuestions'
     * - /shop/dry-hair-treatments -> checks 'dry-hair-treatments', traverses parent chain to 'hair' -> returns 'topHairCareQuestions'
     */

    // Collect all category IDs to check (current, root, and parent chain)
    const categoryIdsToCheck = [];

    if (categoryId) {
        categoryIdsToCheck.push(categoryId);
    }

    if (rootCategoryId) {
        categoryIdsToCheck.push(rootCategoryId);
    }

    // Traverse parent category chain
    let currentParent = parentCategory;

    while (currentParent) {
        if (currentParent.categoryId) {
            categoryIdsToCheck.push(currentParent.categoryId);
        }

        currentParent = currentParent.parentCategory;
    }

    if (categoryIdsToCheck.length === 0) {
        return null;
    }

    // Check if any category ID matches hair categories
    const hairCategories = Categories.GENAI_CATEGORIES_HAIR;

    if (hairCategories.some(cat => categoryIdsToCheck.includes(cat.id))) {
        return 'topHairCareQuestions';
    }

    // Check if any category ID matches skincare categories
    const skincareCategories = Categories.GENAI_CATEGORIES_SKINCARE;

    if (skincareCategories.some(cat => categoryIdsToCheck.includes(cat.id))) {
        return 'topSkincareQuestions';
    }

    // Check if any category ID matches makeup categories
    const makeupCategories = Categories.GENAI_CATEGORIES_MAKEUP;

    if (makeupCategories.some(cat => categoryIdsToCheck.includes(cat.id))) {
        return 'topMakeupQuestions';
    }

    // Check if any category ID matches fragrance categories
    const fragranceCategories = Categories.GENAI_CATEGORIES_FRAGRANCE;

    if (fragranceCategories.some(cat => categoryIdsToCheck.includes(cat.id))) {
        return 'topFragranceQuestions';
    }

    // Check if any category ID matches bath & body categories
    const bathBodyCategories = Categories.GENAI_CATEGORIES_BATH_AND_BODY;

    if (bathBodyCategories.some(cat => categoryIdsToCheck.includes(cat.id))) {
        return 'topBathBodyQuestions';
    }

    return null;
}

function isGenAINewUIEnabled() {
    return Sephora.configurationSettings.isGenAINewUIEnabled;
}

function parsePromptContent(prompt, badgeLabel) {
    const { content, is_new } = prompt || {};
    const safeContent = typeof content === 'string' ? content : '';
    const badgeMatch = /<new>/i.test(safeContent) || is_new;

    if (badgeMatch) {
        const text = safeContent.replace(/<new>/i, '').trim();

        return { text, badge: badgeLabel };
    }

    return { text: safeContent };
}

const DEFAULT_LABEL_TEMPLATES = new Set([
    PageTemplateType.Homepage,
    PageTemplateType.RwdBasket,
    PageTemplateType.ShoppingList,
    PageTemplateType.MyLists,
    PageTemplateType.MyCustomList,
    PageTemplateType.Search,
    PageTemplateType.ProductPage,
    PageTemplateType.RwdContentStore,
    PageTemplateType.ContentStore,
    PageTemplateType.ContentStoreNoNav
]);

function shouldShowDefaultChatLabel(page) {
    const templateType = page?.templateInformation?.template;

    if (templateType) {
        return DEFAULT_LABEL_TEMPLATES.has(templateType);
    }

    return Location.isHomepage() || Location.isBasketPage() || Location.isLovesListPage() || Location.isSearchPage() || Location.isProductPage();
}

function isGenAINewUIEnabledWithRCPS() {
    const isCA = languageLocaleUtils.isCanada();

    const cookieEnabled = isCA ? RCPSCookies.isRCPSBeautyChatCA() : RCPSCookies.isRCPSBeautyChat();

    return cookieEnabled;
}

function getContextAwareLabel(page, placement) {
    if (placement === 'PDP') {
        const product = page?.product;

        if (product?.currentSku?.brandName && product?.currentSku?.productName) {
            return `${product.currentSku.brandName} - ${product.currentSku.productName}`;
        }

        if (product?.currentSku?.brandName) {
            return product.currentSku.brandName;
        }

        if (product?.currentSku?.productName) {
            return product.currentSku.productName;
        }
    }

    if (placement === 'PLP') {
        return page?.nthCategory?.displayName || '';
    }

    if (placement === 'SRP') {
        return getSearchKeyword(page) || '';
    }

    return '';
}

function hasContextChangedFromEntryPoint(page, entryPointData) {
    const currentPlacement = getContextAwarePlacement(page);
    const entryPointPlacement = entryPointData?.entrypoint;

    // CMS entrypoints live on Contentful pages that have no PDP/PLP/SRP placement.
    // Treat them like DEFAULT so their sid/selectedPrompt survive to the initial
    // connection instead of being reset as a false context change.
    const isPlacementlessEntryPoint = entryPointPlacement === ENTRYPOINTS.DEFAULT || entryPointPlacement === ENTRYPOINTS.CMS;

    // If we had a context (PDP/PLP/SRP) and now we don't (Home/other pages), context has changed
    if (!currentPlacement && !isPlacementlessEntryPoint) {
        return true;
    }

    if (!currentPlacement && isPlacementlessEntryPoint) {
        return false;
    }

    if (currentPlacement !== entryPointPlacement) {
        return true;
    }

    if (currentPlacement === 'PLP' && entryPointData?.category_id) {
        return entryPointData.category_id !== page?.nthCategory?.categoryId;
    }

    if (currentPlacement === 'PDP' && entryPointData?.products?.[0]?.product_id) {
        return entryPointData.products[0].product_id !== page?.product?.productId;
    }

    if (currentPlacement === 'SRP' && entryPointData?.search_term) {
        return entryPointData.search_term !== getSearchKeyword(page);
    }

    return false;
}

export {
    isSkincareCategory,
    isAIBeautyChatEnabledPDP,
    isAIBeautyChatEnabledPLP,
    isAIBeautyChatEnabledSRP,
    isGenAINewUIEnabled,
    isGenAINewUIEnabledWithRCPS,
    getUUID,
    getAnonymousId,
    getEcid,
    isAIBeautyGiftEnabled,
    isAIBeautyChatCategoryFragrance,
    getNewEntryPoint,
    getContextAwarePlacement,
    getContextAwareLabel,
    contextHasChanged,
    hasContextChangedFromEntryPoint,
    getCategoryLabelKey,
    shouldShowDefaultChatLabel,
    parsePromptContent,
    getSearchKeyword
};
